import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./SignetPaywall.module.scss"

// ─────────────────────────────────────────────────────────────────────────────
// "Signet" — an ONBOARDING paywall built on COMMITMENT + ENDOWMENT, told as a
// small CEREMONY rather than a feature pitch: you make the vault yours by
// pressing your own seal into warm wax over your memories. A wax seal is honest
// privacy language — it is personal (only your signet makes this mark), it is
// closing (sealed to everyone else), and it is tamper-evident (you can tell if
// anyone ever tried). From the moment it sets, the vault carries YOUR mark and
// opens for only you.
//
// The interaction is a PRESS-AND-HOLD "sealing": hold the signet and molten wax
// blooms out from the press point, fills the medallion, a progress rim closes,
// and at full the wax cools and SETS — an embossed sigil rises and the real
// protections ignite as engravings around the rim. Release early and the wax
// eases back; nothing is committed until you set it. This is not a slider, a
// cipher-scrub, a biometric pad, a tray, or a certificate — it is a deliberate
// act of sealing, and the emotional register is ceremony / quiet pride, not
// fear, locks or countdowns.
//
// Honest & App-Store compliant: the featured, most-prominent price is the yearly
// plan (7-day free trial, per-month broken out, real Save %); monthly is a small
// secondary line. No countdown, no scarcity, no data-loss claim — the wax only
// closes the vault to everyone else; your photos are never deleted or locked
// away from you. Reuses only the house fonts, colour tokens and bundled memory
// photos. Self-contained: renders on debug data, no native injection required.
// ─────────────────────────────────────────────────────────────────────────────

const PUB = process.env.PUBLIC_URL ?? ""
// Reuse the bundled everyday-memory photos — no runtime egress.
const photo = (n: string) => `${PUB}/byheart/${n}.jpg`

// The little fan of "your memories" the seal is pressed over. They peek out from
// under the wax; when it sets, the wax rests over them — closed, not erased.
const MEMORIES = [
  { src: photo("m05"), tilt: -13, x: -58, y: 8, z: 1 },
  { src: photo("m12"), tilt: 11, x: 58, y: 5, z: 1 },
  { src: photo("m02"), tilt: -3, x: 0, y: -4, z: 2 },
]

// Real Premium protections, each engraved around the seal's rim. `t` is the seal
// progress at which the engraving ignites, so they light in sequence as the wax
// sets. Angles place them evenly around the medallion.
const TOKENS: {
  label: string
  angle: number
  t: number
  icon: JSX.Element
}[] = [
  {
    label: "Opens for only you",
    angle: -90,
    t: 0.28,
    icon: (
      <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
        <path
          d="M7 10V7.5a5 5 0 0 1 10 0V10"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <rect
          x="4.8"
          y="10"
          width="14.4"
          height="10"
          rx="2.6"
          fill="currentColor"
        />
        <circle cx="12" cy="14.4" r="1.5" fill="var(--sig-seal-emboss)" />
      </svg>
    ),
  },
  {
    label: "Lifted out of the open roll",
    angle: 0,
    t: 0.5,
    icon: (
      <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
        <rect
          x="3.5"
          y="6.5"
          width="17"
          height="12"
          rx="2.4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <circle
          cx="12"
          cy="12.5"
          r="3"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <path
          d="M8.5 6.5l1.2-2h4.6l1.2 2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    label: "Even the backup stays sealed",
    angle: 90,
    t: 0.72,
    icon: (
      <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
        <path
          d="M12 3.5l7 3v5c0 4.6-3 7.6-7 9-4-1.4-7-4.4-7-9v-5l7-3z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <path
          d="M8.7 12.2l2.2 2.2 4.4-4.6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    label: "You'll know if anyone tries",
    angle: 180,
    t: 0.92,
    icon: (
      <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
        <path
          d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <circle
          cx="12"
          cy="12"
          r="2.7"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
      </svg>
    ),
  },
]

const clamp01 = (n: number) => (n < 0 ? 0 : n > 1 ? 1 : n)

function SignetPaywall() {
  const { state, actions } = useAppState()
  const reduce = useReducedMotion()

  const topInset = state.initialData?.topInset ?? 0
  const bottomInset = state.initialData?.bottomInset ?? 0
  const darkMode = state.initialData?.darkMode ?? false

  const premium = state.productData?.premium
  const yearly = premium?.yearlyProduct
  const monthly = premium?.monthlyProduct
  const locale = yearly?.priceLocale

  const yearlyPrice = yearly ? formatCurrency(yearly.price, locale) : ""
  const perMonth = yearly ? formatCurrency(yearly.price / 12, locale) : ""
  const monthlyPrice = monthly ? formatCurrency(monthly.price, locale) : ""
  const savings = useMemo(() => {
    if (!yearly || !monthly) return 0
    return Math.round((1 - yearly.price / (monthly.price * 12)) * 100)
  }, [yearly, monthly])

  // set: 0 = bare medallion, 1 = the wax has set with your mark.
  const [set, setSet] = useState(reduce ? 1 : 0)
  const [sealed, setSealed] = useState(reduce ? true : false)
  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")
  const [showClose, setShowClose] = useState(false)
  const [committed, setCommitted] = useState(false)

  const setRef = useRef(reduce ? 1 : 0)
  const holdingRef = useRef(false)
  const autoRef = useRef(false) // auto-seal is allowed to run
  const touchedRef = useRef(false) // user grabbed the seal — stop auto
  const sealedRef = useRef(reduce ? true : false)
  const rafRef = useRef<number | null>(null)
  const lastRef = useRef(0)
  const timers = useRef<number[]>([])

  const push = (fn: () => void, ms: number) =>
    timers.current.push(window.setTimeout(fn, ms))

  // Warm the safe-area insets to the ceremony ground so there are no cold bars.
  useEffect(() => {
    actions.setIgnoreHeaderInset(true)
    actions.setIgnoreFooterInset(true)
    actions.setHeaderInsetColor(darkMode ? "#14161f" : "#f2ece1")
    actions.setFooterInsetColor(darkMode ? "#0e1019" : "#efe7d8")
  }, [actions, darkMode])

  const commitSet = useCallback((v: number) => {
    setRef.current = v
    setSet(v)
    if (v >= 1 && !sealedRef.current) {
      sealedRef.current = true
      setSealed(true)
      holdingRef.current = false
    }
  }, [])

  // One rAF engine drives the wax toward a target: filling while held (or during
  // the opening auto-seal), easing back if released early.
  const stopEngine = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = null
    lastRef.current = 0
  }
  const tick = useCallback(
    (ts: number) => {
      if (!lastRef.current) lastRef.current = ts
      const dt = Math.min(48, ts - lastRef.current)
      lastRef.current = ts

      const FILL = dt / 900 // held: ~0.9s to set
      const AUTO = dt / 1900 // hands-off auto seal
      const EASE = dt / 650 // released early: wax relaxes back

      let next = setRef.current
      if (sealedRef.current) {
        stopEngine()
        return
      } else if (holdingRef.current) {
        next += FILL
      } else if (autoRef.current && !touchedRef.current) {
        next += AUTO
      } else {
        next -= EASE
      }
      next = clamp01(next)
      commitSet(next)

      const idle = !holdingRef.current && next <= 0 && !autoRef.current
      if (sealedRef.current || idle) {
        stopEngine()
        return
      }
      rafRef.current = requestAnimationFrame(tick)
    },
    [commitSet],
  )

  const runEngine = useCallback(() => {
    if (rafRef.current == null && !sealedRef.current) {
      lastRef.current = 0
      rafRef.current = requestAnimationFrame(tick)
    }
  }, [tick])

  // On load: reveal the close affordance, then let the seal set itself so
  // passive viewers and screenshots land on the finished, sealed mark.
  useEffect(() => {
    push(() => setShowClose(true), reduce ? 0 : 1500)
    if (reduce) return () => timers.current.forEach(window.clearTimeout)
    push(() => {
      if (!touchedRef.current) {
        autoRef.current = true
        runEngine()
      }
    }, 850)
    return () => {
      stopEngine()
      timers.current.forEach(window.clearTimeout)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduce])

  const takeOver = () => {
    touchedRef.current = true
    autoRef.current = false
  }

  const beginHold = useCallback(() => {
    if (sealedRef.current) return
    takeOver()
    holdingRef.current = true
    runEngine()
  }, [runEngine])

  const endHold = useCallback(() => {
    holdingRef.current = false
    if (!sealedRef.current) runEngine() // ease back down
  }, [runEngine])

  const onPointerDown = (e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture?.(e.pointerId)
    beginHold()
  }
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== " " && e.key !== "Enter") return
    e.preventDefault()
    if (sealedRef.current) return
    takeOver()
    commitSet(clamp01(setRef.current + 0.26)) // each press bites deeper
  }

  const purchase = useCallback(() => {
    const product = plan === "yearly" ? yearly : monthly
    setCommitted(true)
    push(() => {
      if (product) native.subscribe(product)
    }, 720)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, yearly, monthly])

  const handleClose = () => native.postNativeEvent("close")

  const pct = Math.round(set * 100)
  const ctaSub =
    plan === "yearly"
      ? `${yearlyPrice}/year after your 7-day free trial — just ${perMonth}/mo. Renews yearly, cancel anytime.`
      : `${monthlyPrice}/month. Renews monthly, cancel anytime.`

  return (
    <div
      className={`${styles.root} ${darkMode ? styles.isDark : ""} ${
        sealed ? styles.isSealed : ""
      } ${committed ? styles.isCommitted : ""}`}
      style={{ ["--set" as any]: set }}
    >
      <div className={styles.ground} aria-hidden>
        <span className={styles.grain} />
        <span className={styles.halo} />
      </div>

      <div
        className={styles.scroll}
        style={{ paddingTop: topInset + 12, paddingBottom: bottomInset + 14 }}
      >
        {/* Top bar — brand clarity */}
        <div className={styles.topbar}>
          <span className={styles.wordmark}>
            <span className={styles.wmMark} aria-hidden>
              <svg viewBox="0 0 24 24" width="12" height="12">
                <path
                  d="M8 10V7a4 4 0 0 1 8 0v3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <rect
                  x="5.5"
                  y="10"
                  width="13"
                  height="9.5"
                  rx="2.4"
                  fill="currentColor"
                />
              </svg>
            </span>
            PRIVATE&nbsp;PHOTO&nbsp;VAULT
          </span>
          <button
            className={`${styles.close} ${showClose ? styles.closeOn : ""}`}
            onClick={handleClose}
            aria-label="Not now"
            tabIndex={showClose ? 0 : -1}
          >
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Headline */}
        <header className={styles.hero}>
          <p className={styles.eyebrow}>Make the vault yours</p>
          <h1 className={styles.title}>
            Press your seal
            <br />
            <span className={styles.titleThen}>over your memories.</span>
          </h1>
        </header>

        {/* ── The sealing ceremony ─────────────────────────────────────────── */}
        <section
          className={styles.stageWrap}
          aria-label="Set your private seal"
        >
          <div className={styles.stage}>
            {/* the memories the seal is pressed over */}
            <div className={styles.memories} aria-hidden>
              {MEMORIES.map((m, i) => (
                <span
                  key={i}
                  className={styles.memory}
                  style={{
                    ["--tilt" as any]: `${m.tilt}deg`,
                    ["--mx" as any]: `${m.x}px`,
                    ["--my" as any]: `${m.y}px`,
                    zIndex: m.z,
                  }}
                >
                  <img src={m.src} alt="" draggable={false} loading="eager" />
                </span>
              ))}
            </div>

            {/* the signet you press */}
            <button
              className={styles.seal}
              onPointerDown={onPointerDown}
              onPointerUp={endHold}
              onPointerCancel={endHold}
              onPointerLeave={endHold}
              onKeyDown={onKeyDown}
              aria-label="Press and hold to set your seal"
              aria-pressed={sealed}
            >
              {/* progress rim */}
              <span className={styles.rim} aria-hidden />
              {/* molten wax growing from the press point */}
              <span className={styles.wax} aria-hidden>
                <span className={styles.waxGloss} />
              </span>
              {/* embossed sigil that rises as it sets */}
              <span className={styles.sigil} aria-hidden>
                <svg viewBox="0 0 64 64" width="52" height="52">
                  <circle
                    cx="32"
                    cy="32"
                    r="26"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeDasharray="1.5 3.2"
                  />
                  <circle
                    cx="32"
                    cy="32"
                    r="21"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.1"
                  />
                  <path
                    d="M20 30c0-7 5.4-12 12-12s12 5 12 12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M32 21.5c4.6 1.9 7.7 6 7.7 11.4 0 6.5-3.4 10.4-7.7 12-4.3-1.6-7.7-5.5-7.7-12 0-5.4 3.1-9.5 7.7-11.4z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinejoin="round"
                  />
                  <circle cx="32" cy="33.5" r="3.1" fill="currentColor" />
                </svg>
              </span>
              {/* prompt shown before you press */}
              <span className={styles.sealHint}>
                <span className={styles.sealHintRing} aria-hidden />
                Press &amp; hold
              </span>
            </button>
          </div>

          {/* live readout of the ceremony */}
          <div className={styles.readout} aria-live="polite">
            {sealed ? (
              <span className={styles.readSealed}>
                <span className={styles.readDot} />
                Sealed with your mark — opens for <b>only you</b>
              </span>
            ) : (
              <span className={styles.readOpen}>
                {pct > 4
                  ? `Setting your seal — ${pct}%`
                  : "Hold the signet to set your seal"}
              </span>
            )}
          </div>

          {/* real protections — each engraving ignites as the wax sets */}
          <ul className={styles.tokens} aria-label="What your seal protects">
            {TOKENS.map((tk) => (
              <li
                key={tk.label}
                className={`${styles.token} ${
                  set >= tk.t ? styles.tokenLit : ""
                }`}
              >
                <span className={styles.tokenOrb}>{tk.icon}</span>
                <span className={styles.tokenLabel}>{tk.label}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* ── Plans — yearly is the hero ─────────────────────────────────── */}
        <section className={styles.plans} aria-label="Choose your plan">
          <button
            className={`${styles.plan} ${styles.planYear} ${
              plan === "yearly" ? styles.planOn : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            {savings > 0 && (
              <span className={styles.bestTag}>Save {savings}%</span>
            )}
            <span className={styles.planMain}>
              <span className={styles.radio} aria-hidden>
                <span className={styles.radioDot} />
              </span>
              <span className={styles.planTextWrap}>
                <span className={styles.planName}>Sealed for the year</span>
                <span className={styles.planCovers}>
                  7-day free trial, then billed yearly
                </span>
              </span>
              <span className={styles.planPrice}>
                <span className={styles.priceRow}>
                  <span className={styles.priceBig}>{yearlyPrice}</span>
                  <span className={styles.pricePer}>/yr</span>
                </span>
                <span className={styles.pricePm}>{perMonth}/mo</span>
              </span>
            </span>
          </button>

          <button
            className={`${styles.plan} ${styles.planMonth} ${
              plan === "monthly" ? styles.planOn : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span className={styles.planMain}>
              <span className={styles.radio} aria-hidden>
                <span className={styles.radioDot} />
              </span>
              <span className={styles.planTextWrap}>
                <span className={styles.planNameSm}>Monthly</span>
                <span className={styles.planCoversSm}>one month at a time</span>
              </span>
              <span className={styles.planPriceSm}>
                <span className={styles.priceSm}>{monthlyPrice}</span>
                <span className={styles.pricePerSm}>/mo</span>
              </span>
            </span>
          </button>
        </section>

        {/* CTA */}
        <div className={styles.ctaDock}>
          <button
            className={`${styles.cta} ${committed ? styles.ctaCommitted : ""}`}
            onClick={purchase}
          >
            <span className={styles.ctaLabel}>
              {committed ? "Setting your seal…" : "Set my seal — 7 days free"}
            </span>
            {!committed && (
              <svg
                className={styles.ctaArrow}
                viewBox="0 0 24 24"
                width="18"
                height="18"
                aria-hidden
              >
                <path
                  d="M5 12h13M12 5l7 7-7 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
          <p className={styles.ctaSub}>{ctaSub}</p>
          <p className={styles.reassure}>
            Your photos are always yours to keep. The seal only closes the vault
            to everyone else — nothing is ever deleted or locked away from you.
          </p>

          <div className={styles.legal}>
            <button onClick={() => native.postNativeEvent("already_purchased")}>
              Restore
            </button>
            <span>·</span>
            <button onClick={() => native.postNativeEvent("terms")}>
              Terms
            </button>
            <span>·</span>
            <button onClick={() => native.postNativeEvent("privacy")}>
              Privacy
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SignetPaywall
